<template>
  <div v-if="disabled" class="inline-select-text">
    <slot name="disabled-display">
      {{ displayValue }}
    </slot>
  </div>
  <div v-else ref="wrapper" class="inline-select">
    <BushelSelect
      :value="value"
      :options="options"
      :track-by="trackBy"
      :label="label"
      :custom-label="customLabel"
      :loading="loading"
      :searchable="searchable"
      :placeholder="placeholder"
      open-direction="bottom"
      no-padding
      no-deselect
      hide-main-label
      @input-focussed="onOpen"
      @input-blurred="onClose"
      @select="(val) => $emit('select', val)"
    />
  </div>
</template>

<script>
export default {
  props: {
    value: { type: [Object, String, Number], default: null },
    options: { type: Array, required: true },
    trackBy: { type: String, default: "id" },
    label: { type: String, default: "name" },
    customLabel: { type: Function, default: undefined },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    searchable: { type: Boolean, default: true },
    placeholder: { type: String, default: "Select" },
  },
  emits: ["select"],
  computed: {
    displayValue() {
      if (!this.value) return "";
      if (this.customLabel) return this.customLabel(this.value);
      if (typeof this.value === "object" && this.label) return this.value[this.label];
      return String(this.value);
    },
  },
  beforeUnmount() {
    this.removeScrollListeners();
  },
  methods: {
    onOpen() {
      this.$nextTick(() => {
        this.positionDropdown();
        this.addScrollListeners();
      });
    },
    onClose() {
      this.removeScrollListeners();
    },
    positionDropdown() {
      const wrapper = this.$refs.wrapper;
      if (!wrapper) return;

      const dropdown = wrapper.querySelector(".multiselect__content-wrapper");
      if (!dropdown) return;

      const trigger = wrapper.querySelector(".multiselect__tags");
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const margin = 8;

      dropdown.style.setProperty("top", `${rect.bottom}px`, "important");
      dropdown.style.setProperty("left", `${rect.left}px`, "important");
      dropdown.style.setProperty("width", `${Math.max(rect.width, 270)}px`, "important");
      dropdown.style.setProperty("visibility", "visible", "important");
    },
    closeDropdown() {
      const input = this.$refs.wrapper?.querySelector(".multiselect input");
      if (input) input.blur();
    },
    addScrollListeners() {
      let parent = this.$refs.wrapper?.parentElement;
      while (parent) {
        parent.addEventListener("scroll", this.closeDropdown, { passive: true });
        parent = parent.parentElement;
      }
      window.addEventListener("scroll", this.closeDropdown, { passive: true });
    },
    removeScrollListeners() {
      let parent = this.$refs.wrapper?.parentElement;
      while (parent) {
        parent.removeEventListener("scroll", this.closeDropdown);
        parent = parent.parentElement;
      }
      window.removeEventListener("scroll", this.closeDropdown);
    },
  },
};
</script>

<style scoped>
.inline-select-text {
  font-size: 14px;
  line-height: 1.25;
}

.inline-select :deep(.multiselect) {
  font-size: 14px;
  color: #49545a;
  cursor: pointer;
}

.inline-select :deep(.multiselect__single) {
  font-size: 14px !important;
  color: #49545a !important;
  background: transparent !important;
}

.inline-select :deep(.multiselect__tags) {
  border: 1px solid transparent !important;
  box-shadow: none !important;
  background: transparent !important;
}

.inline-select:hover :deep(.multiselect__tags) {
  border: 1px solid #ced4da !important;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075) !important;
  background: #fff !important;
}

.inline-select:hover :deep(.multiselect__single) {
  background: #fff !important;
}

.inline-select :deep(.multiselect--active .multiselect__tags) {
  border: 1px solid #93a3bd !important;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075) !important;
  background: #fff !important;
}

.inline-select :deep(.multiselect__content-wrapper) {
  position: fixed !important;
  z-index: 1050;
  width: auto !important;
  min-width: 220px;
  overflow-x: hidden;
  visibility: hidden;
}
</style>
